'use client';

import React, { useEffect, useRef } from 'react';
import getHTML from '@shared/get-html';
import {
    Box,
    Button,
    HStack,
    Icons,
    Image,
    PlanCard,
    planCardList,
    Text,
    useModal,
    VStack,
    Link,
    type ModalActions,
} from '@shared/ui';
import { getS3ObjectUrl, getStaticImageSource } from 'app/libs/url';
import { PERMISSION_KEY } from 'app/permission';
import { useRouter } from 'next/navigation';
import dayjs from 'dayjs';
import i18n from '@shared/i18n';
import { PLAN_LOCK_FEATURE, PLAN_TYPE } from './constants';
import { getUpgradePrompt, localizedPlanTypeNameMap } from './data';

// const upgradePlanCardListMap = {
//     [PLAN_TYPE.STARTER]: [PLAN_TYPE.BASIC, PLAN_TYPE.STANDARD, PLAN_TYPE.ADVANCED],
//     [PLAN_TYPE.BASIC]: [PLAN_TYPE.STANDARD, PLAN_TYPE.ADVANCED],
//     [PLAN_TYPE.STANDARD]: [PLAN_TYPE.ADVANCED],
// };

const yellowRibbon = getStaticImageSource(
    getS3ObjectUrl('/static/panel-upgrade-overlay-ribbon-yellow_v1.png')
);

const redRibbon = getStaticImageSource(
    getS3ObjectUrl('/static/panel-upgrade-overlay-ribbon-red_v1.png')
);

const salmonRibbon = getStaticImageSource(
    getS3ObjectUrl('/static/panel-upgrade-overlay-ribbon-salmon_v1.png')
);

const greenRibbon = getStaticImageSource(
    getS3ObjectUrl('/static/panel-upgrade-overlay-ribbon-green_v1.png')
);

interface UpgradeUIProps {
    currentPlan: PLAN_TYPE;
    feature?: PLAN_LOCK_FEATURE;
    period: string;
    color?: string;
    permissions: PermissionBEConstant[];
    overlay?: boolean;
    size?: 'base' | 'xs';
}

export const UpgradeCardList: React.FC<UpgradeUIProps & { actions: ModalActions }> = (
    props
) => {
    const { currentPlan, period, feature, actions, permissions } = props;

    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return () => {};
        }

        const handleWheel = (e: WheelEvent) => {
            e.preventDefault();

            container.scrollLeft += e.deltaY;
        };

        container.addEventListener('wheel', handleWheel, { passive: false });

        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, []);

    const router = useRouter();

    const planList = planCardList.map((planDetail) => {
        return {
            isYearly: period === 'yearly',
            onOpen: () => {
                router.push('/panel/plan');
            },
            isLogin: true,
            isSelected: planDetail.planType === currentPlan,
            isUpdatePlan: true,
            enableButton:
                permissions.includes(PERMISSION_KEY.BRAND_SUPER_ADMIN) &&
                planDetail.planType !== currentPlan,
            buttonText: i18n.gettext('查看方案'),
            ...planDetail,
        };
    });

    function handleOnCloseModal() {
        actions.handleNegativeClick();
    }

    const prompt = getUpgradePrompt(currentPlan, feature);

    return (
        <VStack alignItems="stretch">
            <Box py="17px" bg="primary.010" position="relative" overflow="hidden">
                <Box
                    position="absolute"
                    top="50%"
                    left="10px"
                    transform="translateY(-35%)"
                    pointerEvents="none"
                >
                    <Image
                        src={yellowRibbon.src}
                        srcSet={yellowRibbon.srcSet}
                        alt="yellow ribbon"
                        loading="lazy"
                    />
                </Box>
                <Box
                    position="absolute"
                    top="50%"
                    right="0"
                    transform="translateY(-50%)"
                    pointerEvents="none"
                >
                    <Image
                        src={salmonRibbon.src}
                        srcSet={salmonRibbon.srcSet}
                        alt="salmon ribbon"
                        loading="lazy"
                    />
                </Box>

                <Text
                    position="relative"
                    textAlign="center"
                    fontSize="20px"
                    fontWeight="semibold"
                    color="primary.040"
                    lineHeight="1.5"
                >
                    {i18n.gettext('現在就升級方案使用更多功能！')}
                </Text>

                <Box
                    position="absolute"
                    top="50%"
                    left="30px"
                    transform="translateY(-50%)"
                >
                    <Icons.csr.X
                        size="24px"
                        color="neutral.120"
                        onClick={handleOnCloseModal}
                        cursor="pointer"
                        weight="bold"
                    />
                </Box>
            </Box>
            <Box p="23px" pr="0">
                {prompt && (
                    <Box
                        mb="38px"
                        fontSize={{
                            base: '14px',
                            md: '16px',
                        }}
                        fontWeight="semibold"
                        sx={{
                            span: {
                                color: 'dynamic.040',
                                mx: '4px',
                            },
                        }}
                    >
                        {getHTML(prompt, 'p')}
                    </Box>
                )}
                <Box
                    position="relative"
                    _before={{
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        w: '40px',
                        h: '100%',
                        background:
                            'linear-gradient(270deg, #E5E5E5 -39.71%, rgba(238, 238, 239, 0.00) 82.19%)',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                >
                    <HStack gap="0" w="100%" overflowX="auto" ref={containerRef}>
                        {planList.map((plan, index) => (
                            <Box
                                key={`plan-card-${plan.planType}-${index}`}
                                w="272px"
                                flexShrink="0"
                                mr="16px"
                            >
                                <PlanCard {...plan} />
                            </Box>
                        ))}
                    </HStack>
                </Box>
            </Box>
        </VStack>
    );
};

export function useDomainDowngradeReminder(args: {
    deadline: number;
    domain: Domain;
    planType: PLAN_TYPE;
    permissions: PermissionBEConstant[];
}) {
    const { modal } = useModal();

    const deadline = dayjs.unix(args.deadline).format('MM/DD');

    const prompt = i18n
        .gettext(
            '由於品牌的 {plan_type} 已到期或你降級了方案，我們將於 {deadline} 後回復於免費網域 https://{domain}'
        )
        .replace('{plan_type}', localizedPlanTypeNameMap[args.planType])
        .replace('{deadline}', deadline)
        .replace('{domain}', args.domain.domain);

    return modal({
        children: (actions) => (
            <VStack alignItems="stretch">
                <Box
                    py="26px"
                    px="34px"
                    bg="primary.040"
                    position="relative"
                    overflow="hidden"
                >
                    <Box
                        position="absolute"
                        top="50%"
                        right="0"
                        transform="translateY(-50%)"
                        pointerEvents="none"
                    >
                        <Image
                            src={greenRibbon.src}
                            srcSet={greenRibbon.srcSet}
                            alt="salmon ribbon"
                            loading="lazy"
                        />
                    </Box>

                    <Text
                        position="relative"
                        fontSize="24px"
                        fontWeight="semibold"
                        color="neutral.000"
                        lineHeight="1.5"
                    >
                        {i18n.gettext('喔噢！商店的自有網域即將失效')}
                    </Text>
                </Box>
                <Box p="23px">
                    <Box
                        mb="38px"
                        fontSize={{
                            base: '14px',
                            md: '16px',
                        }}
                    >
                        {getHTML(prompt, 'p')}
                    </Box>
                    <Box
                        fontSize={{
                            base: '14px',
                            md: '16px',
                        }}
                        sx={{
                            span: {
                                color: 'dynamic.040',
                                mx: '4px',
                            },
                        }}
                    >
                        {getHTML(
                            i18n.gettext(
                                '若想繼續使用自有網域，請到訂閱<span>升級方案</span>，待方案升級後方可繼續使用。'
                            ),
                            'p'
                        )}
                    </Box>
                    <HStack gap="s" mt="82px" justifyContent="flex-end">
                        {args.permissions.includes(PERMISSION_KEY.BRAND_SUPER_ADMIN) && (
                            <Button
                                as={Link}
                                variant="plain"
                                color="dynamic.040"
                                fontWeight="medium"
                                fontSize="14px"
                                // @ts-ignore
                                href="/panel/subscribe/plan"
                                inline
                            >
                                {i18n.gettext('升級方案')}
                            </Button>
                        )}
                        <Button
                            fontWeight="medium"
                            onClick={actions.handleNegativeClick}
                            inline
                        >
                            {i18n.gettext('我知道了')}
                        </Button>
                    </HStack>
                </Box>
            </VStack>
        ),
        showCloseButton: false,
        sx: {
            w: '100%',
            maxW: '624px',
        },
        containerProps: {
            zIndex: '99999',
        },
    });
}

export function useUpgradeModal(args: UpgradeUIProps) {
    const { modal } = useModal();

    return modal({
        children: (actions) => <UpgradeCardList {...args} actions={actions} />,
        showCloseButton: false,
        sx: {
            w: '100%',
            maxW: '903px',
        },
        containerProps: {
            zIndex: '99999',
        },
    });
}

export const UpgradeButton: React.FC<UpgradeUIProps> = (props) => {
    const {
        currentPlan,
        feature,
        period,
        color = 'dynamic.040',
        overlay = true,
        permissions,
        size = 'xs',
    } = props;

    return (
        <>
            {overlay && (
                <Box
                    position="absolute"
                    inset="0"
                    background="linear-gradient(180deg, rgba(255, 255, 255, 0.50) 7.43%, #FFF 91.52%)"
                    height="100%"
                    pointerEvents="all"
                />
            )}
            <Button
                variant="plain"
                color={color}
                fontWeight="medium"
                p="0 !important"
                fontSize={`${size} !important`}
                onClick={() => {
                    useUpgradeModal({
                        currentPlan,
                        feature,
                        period,
                        permissions,
                    });
                }}
                inline
            >
                {i18n.gettext('升級訂閱方案')}
            </Button>
        </>
    );
};

export const UpgradeOverlay: React.FC<UpgradeUIProps> = (props) => {
    const { currentPlan, feature, period, permissions } = props;

    return (
        <Box
            px={{
                base: '12px',
                md: '27px',
            }}
            py={{
                base: '24px',
                md: '50px',
            }}
        >
            <Box
                position="absolute"
                inset="0"
                background="linear-gradient(180deg, rgba(255, 255, 255, 0.50) 7.43%, #FFF 91.52%)"
                height="100%"
                pointerEvents="all"
            />
            <Box position="relative">
                <Box
                    position="absolute"
                    top="0"
                    left="0"
                    transform="translate(-15%, -110%)"
                >
                    <Image
                        src={yellowRibbon.src}
                        srcSet={yellowRibbon.srcSet}
                        alt="yellow ribbon"
                        loading="lazy"
                    />
                </Box>
                <Box position="absolute" right="0" top="0" transform="translateY(-50%)">
                    <Image
                        src={redRibbon.src}
                        srcSet={redRibbon.srcSet}
                        alt="red ribbon"
                        loading="lazy"
                    />
                </Box>
                <Text
                    position="relative"
                    color="primary.040"
                    fontSize="20px"
                    fontWeight="semibold"
                    mb="xs"
                >
                    {i18n.gettext('升級訂閱方案並解鎖使用')}
                </Text>
                <Button
                    variant="plain"
                    color="dynamic.040"
                    fontWeight="medium"
                    p="0 !important"
                    onClick={() => {
                        useUpgradeModal({
                            currentPlan,
                            feature,
                            period,
                            permissions,
                        });
                    }}
                    inline
                >
                    {i18n.gettext('查看訂閱方案')}
                </Button>
            </Box>
        </Box>
    );
};
